/**
 * SIP softphone UI — registers the logged-in staff member with the Jambonz SBC
 * and renders incoming-call ring + in-call controls, so human-answered SIP
 * calls (AI off) can be taken inside Pulse.
 *
 * Mounted once in App.tsx. Self-gates: if the store isn't on SIP / isn't
 * configured, the credentials endpoint 400s, `connect()` no-ops, and this
 * renders nothing.
 *
 * ⚠️ First integration of @jambonz/client-sdk-web — the call object's event
 * names are bound defensively; verify answer/hangup/mute against a live call.
 */
import { useEffect, useRef, useState } from 'react';

import { api } from '../../lib/api/client';
import { useAuthStore } from '../../stores/authStore';
import {
  softphone,
  type SoftphoneCall,
  type SoftphoneState,
} from './services/jambonzSoftphone';

type TransferStaff = { id: string; name: string; online?: boolean };

const END_EVENTS = ['hangup', 'ended', 'bye', 'failed', 'terminated', 'disconnect'];

/** Play a classic ring cadence (1s on / 2s off) via Web Audio while `active`. */
function useRingtone(active: boolean) {
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let ctx: AudioContext | null = null;
    try {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      ctx = new Ctor();
      void ctx.resume?.();
    } catch {
      return;
    }
    const ring = () => {
      if (stopped || !ctx) return;
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.05);
      gain.gain.setValueAtTime(0.12, now + 0.95);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
      [440, 480].forEach((f) => {
        const osc = ctx!.createOscillator();
        osc.frequency.value = f;
        osc.connect(gain);
        osc.start(now);
        osc.stop(now + 1.0);
      });
    };
    ring();
    const timer = window.setInterval(ring, 3000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      void ctx?.close().catch(() => {});
    };
  }, [active]);
}

export default function SipSoftphone() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [reg, setReg] = useState<SoftphoneState>('idle');
  const [incoming, setIncoming] = useState<SoftphoneCall | null>(null);
  const [active, setActive] = useState<SoftphoneCall | null>(null);
  const [muted, setMuted] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [dialing, setDialing] = useState(false);
  const [held, setHeld] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [staff, setStaff] = useState<TransferStaff[]>([]);
  const [xferNumber, setXferNumber] = useState('');
  // Conference: add staff to the live call (recursive — keep adding people).
  const [showConference, setShowConference] = useState(false);
  const [invited, setInvited] = useState<string[]>([]);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  // Idle widget: collapsed to a draggable button so it doesn't block the
  // mobile chat input.
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  // When we start a conference the backend rings us back into the room; this
  // window tells onIncoming to auto-answer that one leg seamlessly.
  const confRejoinUntilRef = useRef<number>(0);

  useRingtone(!!incoming && !active);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    const clear = () => {
      if (cancelled) return;
      setIncoming(null);
      setActive(null);
      setMuted(false);
    };
    const bindEnd = (call: SoftphoneCall) => {
      END_EVENTS.forEach((ev) => (call as { on?: (e: string, cb: () => void) => void }).on?.(ev, clear));
    };

    softphone.setHandlers({
      onStateChange: (s) => !cancelled && setReg(s),
      onIncoming: (call) => {
        if (cancelled) return;
        bindEnd(call);
        // Our own re-ring into a conference we just started — auto-answer it so
        // the handoff is seamless rather than ringing the agent who clicked.
        if (Date.now() < confRejoinUntilRef.current) {
          confRejoinUntilRef.current = 0;
          try {
            call.answer();
          } catch {
            /* fall through to normal ring if answer throws */
          }
          setActive(call);
          return;
        }
        setIncoming(call);
      },
      onOutgoing: (call) => {
        // Calls placed from anywhere (this widget or the conversation page)
        // surface here as the active call.
        if (cancelled) return;
        bindEnd(call);
        setActive(call);
      },
      onError: (e) => console.warn('[sip-softphone]', e),
    });
    // Register; silently skip when SIP isn't configured (creds endpoint 400s).
    void softphone.connect().catch(() => {});

    return () => {
      cancelled = true;
      softphone.disconnect();
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  const answer = () => {
    incoming?.answer();
    setActive(incoming);
    setIncoming(null);
  };
  const decline = () => {
    incoming?.hangup();
    setIncoming(null);
  };
  const hangup = () => {
    active?.hangup();
    setActive(null);
  };
  const toggleMute = () => {
    active?.toggleMute();
    setMuted((m) => !m);
  };
  const toggleHold = () => {
    if (held) active?.unhold();
    else active?.hold();
    setHeld((h) => !h);
  };
  const loadOnlineStaff = async () => {
    try {
      const all = await api.get<TransferStaff[]>('/voice/transferable-staff');
      // Only offer staff who are online (have Pulse open / softphone reachable).
      setStaff(all.filter((s) => s.online));
    } catch {
      /* leave staff list empty */
    }
  };
  const openTransfer = async () => {
    setShowTransfer(true);
    await loadOnlineStaff();
  };
  const doTransfer = (target: string) => {
    if (!target) {
      console.warn('[sip-softphone] transfer: empty target (domain=%s)', softphone.getDomain());
      alert('Transfer unavailable: SIP domain not set — reload Pulse and try again.');
      return;
    }
    console.info('[sip-softphone] transferring active call to', target);
    try {
      active?.transfer(target); // blind transfer (SIP REFER); call ends on success
    } catch (e) {
      console.error('[sip-softphone] transfer failed', e);
      alert('Transfer failed: ' + (e instanceof Error ? e.message : String(e)));
      return;
    }
    // Let the call's own 'ended' event clear the UI on a successful transfer,
    // rather than assuming success here.
    setShowTransfer(false);
  };
  const openConference = async () => {
    setShowConference(true);
    setInvited([]);
    await loadOnlineStaff();
  };
  // Add a staff member to the live call. The backend moves everyone into a
  // Jambonz conference room server-side and rings the invitee in; the room
  // persists, so this can be repeated to keep adding people (recursive).
  const inviteToConference = async (s: TransferStaff) => {
    if (invitingId) return;
    setInvitingId(s.id);
    // The first invite converts the live call into a room and rings us back in;
    // arm the auto-answer window so that rejoin is seamless. Later invites just
    // add another person — we're already in the room, so no rejoin.
    if (invited.length === 0) confRejoinUntilRef.current = Date.now() + 15000;
    try {
      await api.post('/voice/conference/invite', {
        staffId: s.id,
        fromNumber: active?.from,
      });
      setInvited((prev) => (prev.includes(s.name) ? prev : [...prev, s.name]));
    } catch (e) {
      console.error('[sip-softphone] conference invite failed', e);
      alert('Could not add to call: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setInvitingId(null);
    }
  };
  const handleDial = async () => {
    const num = dialNumber.trim();
    if (!num || dialing) return;
    setDialing(true);
    try {
      // onOutgoing handles surfacing the active call.
      await softphone.dial(num);
      setDialNumber('');
    } catch (e) {
      console.warn('[sip-softphone] dial failed', e);
    } finally {
      setDialing(false);
    }
  };

  if (incoming) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-72 rounded-card border border-line bg-surface p-4 shadow-l">
        <div className="text-sm font-medium text-ink">Incoming call</div>
        <div className="mt-0.5 text-xs text-ink-2">{incoming.from || 'Phone call'}</div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={answer}
            className="flex-1 rounded-pill bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand-ink"
          >
            Answer
          </button>
          <button
            onClick={decline}
            className="flex-1 rounded-pill bg-danger px-3 py-2 text-sm font-medium text-white hover:bg-danger/90"
          >
            Decline
          </button>
        </div>
      </div>
    );
  }

  if (active) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-72 rounded-card border border-line bg-surface p-4 shadow-l">
        <div className="text-sm font-medium text-ink">On call</div>
        <div className="mt-0.5 text-xs text-ink-2">{active.from || 'Phone call'}</div>

        {showTransfer ? (
          <div className="mt-3">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-2">
              Transfer to
            </div>
            <div className="max-h-32 space-y-1 overflow-y-auto">
              {staff.map((s) => (
                <button
                  key={s.id}
                  onClick={() => doTransfer(softphone.staffUri(s.id))}
                  className="block w-full rounded-card border border-line px-2 py-1.5 text-left text-sm text-ink hover:bg-black/[0.04]"
                >
                  {s.name}
                </button>
              ))}
              {staff.length === 0 && (
                <div className="text-xs text-ink-3">No other staff online</div>
              )}
            </div>
            <div className="mt-2 flex gap-2">
              <input
                type="tel"
                value={xferNumber}
                onChange={(e) => setXferNumber(e.target.value)}
                placeholder="or a number"
                className="flex-1 rounded-xl border border-line bg-surface px-2 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-brand/20"
              />
              <button
                onClick={() => doTransfer(xferNumber.trim())}
                disabled={!xferNumber.trim()}
                className="rounded-pill bg-brand px-3 py-1.5 text-sm text-white hover:bg-brand-ink disabled:opacity-50"
              >
                Go
              </button>
            </div>
            <button
              onClick={() => setShowTransfer(false)}
              className="mt-2 text-xs text-ink-3 hover:text-ink"
            >
              Cancel
            </button>
          </div>
        ) : showConference ? (
          <div className="mt-3">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-2">
              Add to call
            </div>
            <div className="max-h-32 space-y-1 overflow-y-auto">
              {staff.map((s) => {
                const added = invited.includes(s.name);
                return (
                  <button
                    key={s.id}
                    onClick={() => void inviteToConference(s)}
                    disabled={added || invitingId === s.id}
                    className="flex w-full items-center justify-between rounded-card border border-line px-2 py-1.5 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-60"
                  >
                    <span>{s.name}</span>
                    <span className="text-xs text-ink-3">
                      {invitingId === s.id ? 'Adding…' : added ? '✓ in call' : 'Add'}
                    </span>
                  </button>
                );
              })}
              {staff.length === 0 && (
                <div className="text-xs text-ink-3">No other staff online</div>
              )}
            </div>
            {invited.length > 0 && (
              <div className="mt-2 text-[11px] text-success">
                In conference: {invited.join(', ')} — add more anytime.
              </div>
            )}
            <button
              onClick={() => setShowConference(false)}
              className="mt-2 text-xs text-ink-3 hover:text-ink"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={toggleMute}
                className="rounded-pill border border-line px-3 py-2 text-sm text-ink hover:bg-black/[0.04]"
              >
                {muted ? 'Unmute' : 'Mute'}
              </button>
              <button
                onClick={toggleHold}
                className="rounded-pill border border-line px-3 py-2 text-sm text-ink hover:bg-black/[0.04]"
              >
                {held ? 'Resume' : 'Hold'}
              </button>
              <button
                onClick={() => void openTransfer()}
                className="rounded-pill border border-line px-3 py-2 text-sm text-ink hover:bg-black/[0.04]"
              >
                Transfer
              </button>
              <button
                onClick={() => void openConference()}
                className="rounded-pill border border-line px-3 py-2 text-sm text-ink hover:bg-black/[0.04]"
              >
                Conference
              </button>
            </div>
            <button
              onClick={hangup}
              className="mt-2 w-full rounded-pill bg-danger px-3 py-2 text-sm font-medium text-white hover:bg-danger/90"
            >
              Hang up
            </button>
          </>
        )}
      </div>
    );
  }

  if (reg === 'registered') {
    const onPointerDown = (e: React.PointerEvent) => {
      dragRef.current = { x: e.clientX, y: e.clientY, moved: false };
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 6) d.moved = true;
      if (d.moved) setPos({ x: e.clientX - 22, y: e.clientY - 22 });
    };
    const onPointerUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      if (d && !d.moved) setExpanded((v) => !v); // tap (not drag) toggles
    };
    const style = pos
      ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' as const }
      : undefined;
    // Default sits above the mobile chat input; draggable anywhere after that.
    const posClass = pos ? 'fixed' : 'fixed bottom-24 right-4 sm:bottom-4';

    if (!expanded) {
      return (
        <button
          style={style}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          title="Phone ready — tap to dial, drag to move"
          className={`${posClass} z-40 flex h-11 w-11 touch-none items-center justify-center rounded-full border border-line bg-surface shadow-l`}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-signal" />
        </button>
      );
    }
    return (
      <div style={style} className={`${posClass} z-40 w-64 rounded-card border border-line bg-surface p-3 shadow-l`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] text-ink-2">
            <span className="h-1.5 w-1.5 rounded-full bg-signal" /> Phone ready
          </div>
          <button onClick={() => setExpanded(false)} className="px-1 text-ink-3 hover:text-ink" title="Minimize">
            ▾
          </button>
        </div>
        <div className="mt-2 flex gap-2">
          <input
            type="tel"
            value={dialNumber}
            onChange={(e) => setDialNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void handleDial()}
            placeholder="Number to call"
            className="flex-1 rounded-xl border border-line bg-surface px-2 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-brand/20"
          />
          <button
            onClick={() => void handleDial()}
            disabled={!dialNumber.trim() || dialing}
            className="rounded-pill bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-ink disabled:opacity-50"
          >
            {dialing ? '…' : 'Call'}
          </button>
        </div>
      </div>
    );
  }
  return null;
}
